import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { makeRowColumn, getHeadObj, getDate } from 'lib/commonLib';
import { paramConverter } from 'modules/common';
import { dailyCampaign } from 'lib/api/time';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import NewCommonFilter from 'components/organisms/NewCommonFilter';

import CampaignStatisticsGraph from './CampaignStatisticsGraph';

const useStyles = makeStyles((theme) => ({
  labelBox: {
    width: 250,
  },
  contentbox: {
    width: '100%',
    display: 'flex',
    '& > div': {
      textAlign: 'center',
      width: '33.3%',
    },
  },
}));

const CampaignStatistics = () => {
  let initParams = {
    pltfomTpCode: 'all',
    sort: 'desc',
    order: 'STATS_DTTM',
    sDate: getDate(moment(moment().format('YYYY-MM-01'))),
    eDate: moment().add(-1, 'day').format('YYYYMMDD'),
  };

  const { t } = useTranslation();
  const classes = useStyles();

  const [params, setParams] = useState(initParams);
  const [initialization, setInitialization] = useState(false);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  // header 및 body 컬럼 커스텀
  const customized = {
    STATS_DTTM: {
      makeHead: (headsMap) => {
        return `${getHeadObj(headsMap, 'STATS_DTTM').label}`;
      },
      makeBody: (rowObj, headsMap) => {
        const statsDttm = getHeadObj(headsMap, 'STATS_DTTM');
        const dateValue = makeRowColumn(
          rowObj['STATS_DTTM'],
          'date',
          'YYYY-MM-DD'
        );
        var dayOfWeek = new Date(dateValue).getDay();
        let resultComponent;
        let dayColor;
        if (dayOfWeek === 0) {
          dayColor = 'red';
        } else if (dayOfWeek === 6) {
          dayColor = 'blue';
        }

        if (rowObj.isChild) {
          resultComponent = (
            <>
              {makeRowColumn(
                rowObj['STATS_DTTM'],
                statsDttm.type,
                statsDttm.format
              )}
            </>
          );
        } else {
          resultComponent = (
            <IconAreaWrap
              text={makeRowColumn(
                rowObj['STATS_DTTM'],
                statsDttm.type,
                statsDttm.format
              )}
              textColor={dayColor}
            />
          );
        }
        return resultComponent;
      },
    },
    ADVER_CNT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={getHeadObj(headsMap, 'ADVER_CNT').label}
                tooltipTitle={t('지출금액 1,000 이상인 광고주 수')}
                iconType="fainfoCircle"
              />
            </>
          ),
        };
      },
      makeBody: (rowObj, headsMap) => {
        const adverCnt = getHeadObj(headsMap, 'ADVER_CNT');
        return (
          <>
            {makeRowColumn(rowObj['ADVER_CNT'], adverCnt.type, adverCnt.format)}
          </>
        );
      },
    },
    MOB_TOT_CAMP_CNT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <div>
                <div className={classes.labelBox}>
                  {getHeadObj(headsMap, 'MOB_TOT_CAMP_CNT').label}
                </div>
                <div className={classes.contentbox}>
                  <div>total</div>
                  <div>{t('제한')}</div>
                  <div>{t('무제한')}</div>
                </div>
              </div>
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const totCampCnt = getHeadObj(headsMap, 'MOB_TOT_CAMP_CNT');
        const limitCampCnt = getHeadObj(headsMap, 'LIMIT_CAMP_CNT');
        const unLimitCampCnt = getHeadObj(headsMap, 'UN_LIMIT_CAMP_CNT');
        return (
          <>
            <div className={classes.contentbox}>
              <div>
                {makeRowColumn(
                  rowObj['MOB_TOT_CAMP_CNT'],
                  totCampCnt.type,
                  totCampCnt.format
                )}
              </div>
              <div>
                {makeRowColumn(
                  rowObj['LIMIT_CAMP_CNT'],
                  limitCampCnt.type,
                  limitCampCnt.format
                )}
              </div>
              <div>
                {makeRowColumn(
                  rowObj['UN_LIMIT_CAMP_CNT'],
                  unLimitCampCnt.type,
                  unLimitCampCnt.format
                )}
              </div>
            </div>
          </>
        );
      },
    },
    LIMIT_CAMP_CNT: {
      merged: true,
    },
    UN_LIMIT_CAMP_CNT: {
      merged: true,
    },
    FILTER_BDGT_AMT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <div>
                <div className={classes.labelBox}>
                  {t('TOTAL(모비온+카카오)')}
                </div>
                <div className={classes.contentbox}>
                  <div style={{ width: '50%' }}>{t('소진율 1% 필터예산')}</div>
                  <div style={{ width: '50%' }}>{t('총 지출금액')}</div>
                </div>
              </div>
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const filterBdgtAmt = getHeadObj(headsMap, 'FILTER_BDGT_AMT');
        const campTotAdvrtsAmt = getHeadObj(headsMap, 'CAMP_TOT_ADVRTS_AMT');
        return (
          <>
            <div className={classes.contentbox}>
              <div style={{ width: '50%' }}>
                {makeRowColumn(
                  rowObj['FILTER_BDGT_AMT'],
                  filterBdgtAmt.type,
                  filterBdgtAmt.format
                )}
              </div>
              <div style={{ width: '50%' }}>
                {makeRowColumn(
                  rowObj['CAMP_TOT_ADVRTS_AMT'],
                  campTotAdvrtsAmt.type,
                  campTotAdvrtsAmt.format
                )}
              </div>
            </div>
          </>
        );
      },
    },
    CAMP_TOT_ADVRTS_AMT: {
      merged: true,
    },
    PRDT_TGRT_FILTER_BDGT_AMT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <div>
                <div className={classes.labelBox}>{t('상품타겟팅')}</div>
                <div className={classes.contentbox}>
                  <div style={{ width: '50%' }}>{t('필터예산')}</div>
                  <div style={{ width: '50%' }}>{t('지출금액')}</div>
                </div>
              </div>
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const prdtTrgtFilterBdgtAmt = getHeadObj(
          headsMap,
          'PRDT_TGRT_FILTER_BDGT_AMT'
        );
        const prdtTrgtAdvrtsAmt = getHeadObj(headsMap, 'PRDT_TRGT_ADVRTS_AMT');
        return (
          <>
            <div className={classes.contentbox}>
              <div style={{ width: '50%' }}>
                {makeRowColumn(
                  rowObj['PRDT_TGRT_FILTER_BDGT_AMT'],
                  prdtTrgtFilterBdgtAmt.type,
                  prdtTrgtFilterBdgtAmt.format
                )}
              </div>
              <div style={{ width: '50%' }}>
                {makeRowColumn(
                  rowObj['PRDT_TRGT_ADVRTS_AMT'],
                  prdtTrgtAdvrtsAmt.type,
                  prdtTrgtAdvrtsAmt.format
                )}
              </div>
            </div>
          </>
        );
      },
    },
    PRDT_TRGT_ADVRTS_AMT: {
      merged: true,
    },
    USER_TRGT_FILTER_BDGT_AMT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <div>
                <div className={classes.labelBox}>{t('유저타겟팅')}</div>
                <div className={classes.contentbox}>
                  <div style={{ width: '50%' }}>{t('필터예산')}</div>
                  <div style={{ width: '50%' }}>{t('지출금액')}</div>
                </div>
              </div>
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const userTrgtFilterBdgtAmt = getHeadObj(
          headsMap,
          'USER_TRGT_FILTER_BDGT_AMT'
        );
        const userTrgtAdvrtsAmt = getHeadObj(headsMap, 'USER_TRGT_ADVRTS_AMT');
        return (
          <>
            <div className={classes.contentbox}>
              <div style={{ width: '50%' }}>
                {makeRowColumn(
                  rowObj['USER_TRGT_FILTER_BDGT_AMT'],
                  userTrgtFilterBdgtAmt.type,
                  userTrgtFilterBdgtAmt.format
                )}
              </div>
              <div style={{ width: '50%' }}>
                {makeRowColumn(
                  rowObj['USER_TRGT_ADVRTS_AMT'],
                  userTrgtAdvrtsAmt.type,
                  userTrgtAdvrtsAmt.format
                )}
              </div>
            </div>
          </>
        );
      },
    },
    USER_TRGT_ADVRTS_AMT: {
      merged: true,
    },
    NON_PRDT_TRGT_FILTER_BDGT_AMT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <div>
                <div className={classes.labelBox}>{t('논타겟팅')}</div>
                <div className={classes.contentbox}>
                  <div style={{ width: '50%' }}>{t('필터예산')}</div>
                  <div style={{ width: '50%' }}>{t('지출금액')}</div>
                </div>
              </div>
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const nonPrdtTrgtfilterBdgtAmt = getHeadObj(
          headsMap,
          'NON_PRDT_TRGT_FILTER_BDGT_AMT'
        );
        const nonPrdtTrgtAdvrtsAmt = getHeadObj(
          headsMap,
          'NON_PRDT_TRGT_ADVRTS_AMT'
        );

        return (
          <>
            <div className={classes.contentbox}>
              <div style={{ width: '50%' }}>
                {makeRowColumn(
                  rowObj['NON_PRDT_TRGT_FILTER_BDGT_AMT'],
                  nonPrdtTrgtfilterBdgtAmt.type,
                  nonPrdtTrgtfilterBdgtAmt.format
                )}
              </div>
              <div style={{ width: '50%' }}>
                {makeRowColumn(
                  rowObj['NON_PRDT_TRGT_ADVRTS_AMT'],
                  nonPrdtTrgtAdvrtsAmt.type,
                  nonPrdtTrgtAdvrtsAmt.format
                )}
              </div>
            </div>
          </>
        );
      },
    },
    NON_PRDT_TRGT_ADVRTS_AMT: {
      merged: true,
    },
    TOT_BDGT_AMT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <div>
                <div className={classes.labelBox}>
                  {t('모비온 일허용 예산')}
                </div>
                <div className={classes.contentbox}>
                  <div>total</div>
                  <div>{t('제한')}</div>
                  <div>{t('무제한')}</div>
                </div>
              </div>
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const totBdgtAmt = getHeadObj(headsMap, 'TOT_BDGT_AMT');
        const limitBdgtAmt = getHeadObj(headsMap, 'LIMIT_BDGT_AMT');
        const unLimitBdgtAmt = getHeadObj(headsMap, 'UN_LIMIT_BDGT_AMT');

        return (
          <>
            <div className={classes.contentbox}>
              <div>
                {makeRowColumn(
                  rowObj['TOT_BDGT_AMT'],
                  totBdgtAmt.type,
                  totBdgtAmt.format
                )}
              </div>
              <div>
                {makeRowColumn(
                  rowObj['LIMIT_BDGT_AMT'],
                  limitBdgtAmt.type,
                  limitBdgtAmt.format
                )}
              </div>
              <div>
                {makeRowColumn(
                  rowObj['UN_LIMIT_BDGT_AMT'],
                  unLimitBdgtAmt.type,
                  unLimitBdgtAmt.format
                )}
              </div>
            </div>
          </>
        );
      },
    },
    LIMIT_BDGT_AMT: {
      merged: true,
    },
    UN_LIMIT_BDGT_AMT: {
      merged: true,
    },
    CAMP_MOB_TOT_ADVRTS_AMT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <div>
                <div className={classes.labelBox} style={{ width: 350 }}>
                  {t('모비온 지출금액')}
                </div>
                <div className={classes.contentbox} style={{ width: 350 }}>
                  <div style={{ width: '25%' }}>total</div>
                  <div style={{ width: '25%' }}>{t('제한')}</div>
                  <div style={{ width: '25%' }}>{t('무제한')}</div>
                  <div style={{ width: '25%' }}>{t('기타')}</div>
                </div>
              </div>
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const campMobTotAdvrtsAmt = getHeadObj(
          headsMap,
          'CAMP_MOB_TOT_ADVRTS_AMT'
        );
        const limitAdvrtsAmt = getHeadObj(headsMap, 'LIMIT_ADVRTS_AMT');
        const unLimitAdvrtsAmt = getHeadObj(headsMap, 'UN_LIMIT_ADVRTS_AMT');
        const etcAdvrtsAmt = getHeadObj(headsMap, 'ETC_ADVRTS_AMT');

        return (
          <>
            <div className={classes.contentbox} style={{ width: 350 }}>
              <div style={{ width: '25%' }}>
                {makeRowColumn(
                  rowObj['CAMP_MOB_TOT_ADVRTS_AMT'],
                  campMobTotAdvrtsAmt.type,
                  campMobTotAdvrtsAmt.format
                )}
              </div>
              <div style={{ width: '25%' }}>
                {makeRowColumn(
                  rowObj['LIMIT_ADVRTS_AMT'],
                  limitAdvrtsAmt.type,
                  limitAdvrtsAmt.format
                )}
              </div>
              <div style={{ width: '25%' }}>
                {makeRowColumn(
                  rowObj['UN_LIMIT_ADVRTS_AMT'],
                  unLimitAdvrtsAmt.type,
                  unLimitAdvrtsAmt.format
                )}
              </div>
              <div style={{ width: '25%' }}>
                {makeRowColumn(
                  rowObj['ETC_ADVRTS_AMT'],
                  etcAdvrtsAmt.type,
                  etcAdvrtsAmt.format
                )}
              </div>
            </div>
          </>
        );
      },
    },
    LIMIT_ADVRTS_AMT: {
      merged: true,
    },
    UN_LIMIT_ADVRTS_AMT: {
      merged: true,
    },
    ETC_ADVRTS_AMT: {
      merged: true,
    },
    KAKAO_TOT_CAMP_CNT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <div>
                <div className={classes.labelBox}>{t('카카오 캠페인 수')}</div>
                <div className={classes.contentbox}>
                  <div>total</div>
                  <div>{t('제한')}</div>
                  <div>{t('무제한')}</div>
                </div>
              </div>
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const kakaoTotCampCnt = getHeadObj(headsMap, 'KAKAO_TOT_CAMP_CNT');
        const kakaoLimitCampCnt = getHeadObj(headsMap, 'KAKAO_LIMIT_CAMP_CNT');
        const kakaoUnLimitCampCnt = getHeadObj(
          headsMap,
          'KAKAO_UN_LIMIT_CAMP_CNT'
        );

        return (
          <>
            <div className={classes.contentbox}>
              <div>
                {makeRowColumn(
                  rowObj['KAKAO_TOT_CAMP_CNT'],
                  kakaoTotCampCnt.type,
                  kakaoTotCampCnt.format
                )}
              </div>
              <div>
                {makeRowColumn(
                  rowObj['KAKAO_LIMIT_CAMP_CNT'],
                  kakaoLimitCampCnt.type,
                  kakaoLimitCampCnt.format
                )}
              </div>
              <div>
                {makeRowColumn(
                  rowObj['KAKAO_UN_LIMIT_CAMP_CNT'],
                  kakaoUnLimitCampCnt.type,
                  kakaoUnLimitCampCnt.format
                )}
              </div>
            </div>
          </>
        );
      },
    },
    KAKAO_UN_LIMIT_CAMP_CNT: {
      merged: true,
    },
    KAKAO_LIMIT_CAMP_CNT: {
      merged: true,
    },
    KAKAO_TOT_BDGT_AMT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <div>
                <div className={classes.labelBox}>{t('카카오 일허용예산')}</div>
                <div className={classes.contentbox}>
                  <div>total</div>
                  <div>{t('제한')}</div>
                  <div>{t('무제한')}</div>
                </div>
              </div>
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const kakaoTotBdgtAmt = getHeadObj(headsMap, 'KAKAO_TOT_BDGT_AMT');
        const kakaoLimitBdgtAmt = getHeadObj(headsMap, 'KAKAO_LIMIT_BDGT_AMT');
        const kakaoUnLimitBdgtAmt = getHeadObj(
          headsMap,
          'KAKAO_UN_LIMIT_BDGT_AMT'
        );

        return (
          <>
            <div className={classes.contentbox}>
              <div>
                {makeRowColumn(
                  rowObj['KAKAO_TOT_BDGT_AMT'],
                  kakaoTotBdgtAmt.type,
                  kakaoTotBdgtAmt.format
                )}
              </div>
              <div>
                {makeRowColumn(
                  rowObj['KAKAO_LIMIT_BDGT_AMT'],
                  kakaoLimitBdgtAmt.type,
                  kakaoLimitBdgtAmt.format
                )}
              </div>
              <div>
                {makeRowColumn(
                  rowObj['KAKAO_UN_LIMIT_BDGT_AMT'],
                  kakaoUnLimitBdgtAmt.type,
                  kakaoUnLimitBdgtAmt.format
                )}
              </div>
            </div>
          </>
        );
      },
    },
    KAKAO_LIMIT_BDGT_AMT: {
      merged: true,
    },
    KAKAO_UN_LIMIT_BDGT_AMT: {
      merged: true,
    },
    KAKAO_TOT_ADVRTS_AMT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <div>
                <div className={classes.labelBox} style={{ width: 350 }}>
                  {t('카카오 지출금액')}
                </div>
                <div className={classes.contentbox} style={{ width: 350 }}>
                  <div style={{ width: '25%' }}>total</div>
                  <div style={{ width: '25%' }}>{t('제한')}</div>
                  <div style={{ width: '25%' }}>{t('무제한')}</div>
                  <div style={{ width: '25%' }}>{t('기타')}</div>
                </div>
              </div>
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const kakaoTotAdvrtsAmt = getHeadObj(headsMap, 'KAKAO_TOT_ADVRTS_AMT');
        const kakaoLimitAdvrtsAmt = getHeadObj(
          headsMap,
          'KAKAO_LIMIT_ADVRTS_AMT'
        );
        const kakaoUnLimitAdvrtsAmt = getHeadObj(
          headsMap,
          'KAKAO_UN_LIMIT_ADVRTS_AMT'
        );
        const kakaoEtcAdvrtsAmt = getHeadObj(headsMap, 'KAKAO_ETC_ADVRTS_AMT');

        return (
          <>
            <div className={classes.contentbox} style={{ width: 350 }}>
              <div style={{ width: '25%' }}>
                {makeRowColumn(
                  rowObj['KAKAO_TOT_ADVRTS_AMT'],
                  kakaoTotAdvrtsAmt.type,
                  kakaoTotAdvrtsAmt.format
                )}
              </div>
              <div style={{ width: '25%' }}>
                {makeRowColumn(
                  rowObj['KAKAO_LIMIT_ADVRTS_AMT'],
                  kakaoLimitAdvrtsAmt.type,
                  kakaoLimitAdvrtsAmt.format
                )}
              </div>
              <div style={{ width: '25%' }}>
                {makeRowColumn(
                  rowObj['KAKAO_UN_LIMIT_ADVRTS_AMT'],
                  kakaoUnLimitAdvrtsAmt.type,
                  kakaoUnLimitAdvrtsAmt.format
                )}
              </div>
              <div style={{ width: '25%' }}>
                {makeRowColumn(
                  rowObj['KAKAO_ETC_ADVRTS_AMT'],
                  kakaoEtcAdvrtsAmt.type,
                  kakaoEtcAdvrtsAmt.format
                )}
              </div>
            </div>
          </>
        );
      },
    },
    KAKAO_LIMIT_ADVRTS_AMT: {
      merged: true,
    },
    KAKAO_UN_LIMIT_ADVRTS_AMT: {
      merged: true,
    },
    KAKAO_ETC_ADVRTS_AMT: {
      merged: true,
    },
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    setInitialization(true);
    setParams(initParams);
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={params}
          getParam={getParam}
          useExternalFilter={false}
        />
      </NewFilterWrap>

      <>
        <CampaignStatisticsGraph params={{ ...paramConverter(params) }} />

        <NewTableContainer
          params={params}
          getParam={getParam}
          dataReqPromise={dailyCampaign}
          customized={customized}
          rowsPerPage={-1}
          useFetch={false}
          useTotal={false}
          useSearch={false}
          usePagenation={false}
        />
      </>
    </>
  );
};

export default CampaignStatistics;
